import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import { Gift } from "lucide-react";
import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import {
  ExperienceFieldGuide,
  ExperienceFieldLabel,
  ExperienceHeader,
  ExperienceMediaCard,
  ExperienceOptionalLabel,
  ExperienceShell,
  type ExperienceMomentVariant,
  fieldHelperText,
  getExperienceFieldSx,
  getExperienceTheme,
  getFloatingActionSx,
  getPrimaryButtonSx,
  isSeasonMomentVariant,
} from "./ExperienceUI";
import { useCurrentTalentProfile } from "@/shared/stores/useProfile";
import { useGetPublishedCards } from "../../api/taascard.api";

interface LinkedCard {
  card_id?: number | string;
  card_name?: string;
  card_type?: string;
  thumbnail_url?: string;
}

interface BonusData {
  title?: string;
  description?: string;
  voiceNote?: string | File;
  image?: string | File;
  demoSnippet?: string | File;
  cardLinks?: LinkedCard[];
}

interface BonusExperienceProps {
  darkMode: boolean;
  data?: BonusData;
  momentVariant?: ExperienceMomentVariant;
  onDataChange?: (data: BonusData) => void;
}

const hasMediaValue = (value?: string | File) =>
  value instanceof File || (typeof value === "string" && value.trim() !== "");

const sanitizeString = (value: unknown): string => {
  if (typeof value !== "string") return "";
  const sanitized = value.trim();
  if (
    !sanitized ||
    sanitized.toLowerCase() === "string" ||
    sanitized.toLowerCase() === "null" ||
    sanitized.toLowerCase() === "undefined"
  ) {
    return "";
  }
  return sanitized;
};

const normalizeLinkedCard = (card: any): LinkedCard => ({
  card_id: card?.card_id ?? card?.cardId ?? card?.id ?? undefined,
  card_name:
    sanitizeString(card?.card_name) ||
    sanitizeString(card?.cardName) ||
    sanitizeString(card?.title),
  card_type:
    sanitizeString(card?.card_type) ||
    sanitizeString(card?.cardType) ||
    sanitizeString(card?.subtitle),
  thumbnail_url:
    sanitizeString(card?.thumbnail_url) ||
    sanitizeString(card?.thumbnailUrl) ||
    sanitizeString(card?.thumbnail),
});

const normalizeLinkedCards = (cards: unknown): LinkedCard[] =>
  Array.isArray(cards)
    ? cards
        .map(normalizeLinkedCard)
        .filter(
          (card) =>
            card.card_id ||
            card.card_name ||
            card.card_type ||
            card.thumbnail_url
        )
    : [];

const formatCardTypeLabel = (cardType?: string) =>
  sanitizeString(cardType)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const normalizeCardTypeKey = (cardType?: string): string =>
  sanitizeString(cardType).toLowerCase().replace(/\s+/g, "_");

const allowedLinkedCardTypes = new Set([
  "k_indie_card",
  "busking_card",
  "single_card",
  "gift_card",
]);

const getLinkedCardKey = (card: LinkedCard, index: number) =>
  card.card_id != null
    ? String(card.card_id)
    : `${card.card_name || "card"}-${index}`;

const stripRichText = (value: string | undefined | null): string =>
  (value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const getValidationSchema = (t: any, isSeason: boolean) =>
  isSeason
    ? Yup.object({
        title: Yup.string().nullable(),
        description: Yup.string().nullable(),
        voiceNote: Yup.mixed().nullable(),
        image: Yup.mixed().nullable(),
        demoSnippet: Yup.mixed().nullable(),
      }).test(
        "bonus-has-content",
        t("bonus_content_required_season"),
        function hasBonusContent() {
          const values = this.parent as BonusData;
          return (
            stripRichText(values.title).length > 0 ||
            stripRichText(values.description).length > 0 ||
            hasMediaValue(values.voiceNote as string | File | undefined) ||
            hasMediaValue(values.image as string | File | undefined) ||
            hasMediaValue(values.demoSnippet as string | File | undefined) ||
            (Array.isArray(values.cardLinks) && values.cardLinks.length > 0)
          );
        },
      )
    : Yup.object({
    title: Yup.string()
      .required(t("title_is_required"))
      .min(3, t("title_must_be_at_least_3_characters")),
    description: Yup.string()
      .required(t("description_is_required"))
      .min(10, t("description_must_be_at_least_10_characters")),
    voiceNote: Yup.mixed().test(
      "voice-note-required",
      t("audio_is_required"),
      (value) => hasMediaValue(value as string | File | undefined)
    ),
    image: Yup.mixed().test(
      "bonus-image-required",
      t("image_is_required"),
      (value) => hasMediaValue(value as string | File | undefined)
    ),
    demoSnippet: Yup.mixed().test(
      "demo-snippet-required",
      t("audio_is_required"),
      (value) => hasMediaValue(value as string | File | undefined)
    ),
  });

const BonusExperience: React.FC<BonusExperienceProps> = ({
  darkMode,
  data,
  momentVariant = "standard",
  onDataChange,
}) => {
  const { t } = useTranslation("taascard");
  const isSeason = isSeasonMomentVariant(momentVariant);
  const theme = getExperienceTheme(darkMode);
  const fieldSx = getExperienceFieldSx(darkMode);
  const { currentProfile } = useCurrentTalentProfile();
  const validationSchema = getValidationSchema(t, isSeason);
  const isInternalUpdateRef = useRef(false);
  const isSyncingFromParentRef = useRef(false);
  const { data: cardsData, isLoading: isCardsLoading } = useGetPublishedCards({
    key: [
      "bonusLinkedCards",
      currentProfile?.id ?? "",
    ],
    params: {
      headers: {
        ...(currentProfile?.id && { "talent-id": currentProfile?.id }),
      },
    },
  });

  const resolvedData = useMemo<BonusData>(
    () => ({
      title: data?.title ?? (isSeason ? "" : t("bonus_default_title")),
      description: data?.description ?? (isSeason ? "" : t("bonus_default_description")),
      voiceNote: data?.voiceNote ?? "",
      image: data?.image ?? "",
      demoSnippet: data?.demoSnippet ?? "",
      cardLinks: normalizeLinkedCards(data?.cardLinks),
    }),
    [
      data?.title,
      data?.description,
      data?.voiceNote,
      data?.image,
      data?.demoSnippet,
      data?.cardLinks,
      isSeason,
      t,
    ]
  );

  const availableCards = useMemo<LinkedCard[]>(() => {
    const apiCards = cardsData?.data || [];

    return apiCards
      .map((card: any) => {
        const firstPage = card?.pages?.[0];
        const content = firstPage?.content || {};
        const thumbnailFromContent = Array.isArray(content?.url)
          ? sanitizeString(content.url[0])
          : sanitizeString(content?.url);

        return normalizeLinkedCard({
          card_id: card?.id,
          card_name:
            sanitizeString(content?.title) ||
            sanitizeString(content?.name) ||
            sanitizeString(content?.description) ||
            t("bonus_card_with_id", { id: card?.id ?? "" }).trim(),
          card_type:
            sanitizeString(content?.type) ||
            sanitizeString(firstPage?.type) ||
            sanitizeString(card?.template),
          thumbnail_url:
            sanitizeString(card?.cover_image_url) ||
            sanitizeString(content?.thumbnail) ||
            sanitizeString(content?.imageUrl) ||
            thumbnailFromContent,
        });
      })
      .filter(
        (card: LinkedCard) =>
          card.card_id != null &&
          card.card_name &&
          allowedLinkedCardTypes.has(normalizeCardTypeKey(card.card_type))
      );
  }, [cardsData, t]);

  const formik = useFormik<BonusData>({
    initialValues: resolvedData,
    enableReinitialize: true,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: !isSeason,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (!availableCards.length) return;

    const currentLinks = normalizeLinkedCards(formik.values.cardLinks);
    const nextLinks = currentLinks.map((linkedCard) => {
      const matchedCard = availableCards.find((option) =>
        linkedCard.card_id != null
          ? String(option.card_id) === String(linkedCard.card_id)
          : option.card_name === linkedCard.card_name &&
            option.card_type === linkedCard.card_type
      );

      if (!matchedCard) return linkedCard;

      return {
        ...linkedCard,
        card_id: linkedCard.card_id ?? matchedCard.card_id,
        card_name: linkedCard.card_name || matchedCard.card_name,
        card_type: linkedCard.card_type || matchedCard.card_type,
        thumbnail_url: linkedCard.thumbnail_url || matchedCard.thumbnail_url,
      };
    });

    if (JSON.stringify(currentLinks) !== JSON.stringify(nextLinks)) {
      formik.setFieldValue("cardLinks", nextLinks, false);
    }
  }, [availableCards, formik.values.cardLinks]);

  useEffect(() => {
    if (data && !isInternalUpdateRef.current) {
      const hasChanged = (
        ["title", "description", "voiceNote", "image", "demoSnippet"] as Array<
          keyof BonusData
        >
      ).some((key) => formik.values[key] !== resolvedData[key]);

      const linksChanged =
        JSON.stringify(formik.values.cardLinks || []) !==
        JSON.stringify(resolvedData.cardLinks || []);

      if (hasChanged || linksChanged) {
        isSyncingFromParentRef.current = true;
        formik.setValues(resolvedData);
      }
    }
    isInternalUpdateRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, resolvedData]);

  useEffect(() => {
    if (!onDataChange) return;
    if (isSyncingFromParentRef.current) {
      const isSynced =
        JSON.stringify(formik.values) === JSON.stringify(resolvedData);
      if (isSynced) {
        isSyncingFromParentRef.current = false;
      }
      return;
    }
    const hasChangedFromParent =
      JSON.stringify(formik.values) !== JSON.stringify(resolvedData);
    if (!hasChangedFromParent) return;

    const timeoutId = setTimeout(() => {
      isInternalUpdateRef.current = true;
      onDataChange({
        ...formik.values,
        cardLinks: normalizeLinkedCards(formik.values.cardLinks),
      });
    }, 0);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values, onDataChange, resolvedData]);

  const handleFileSelect = (
    files: FileList | null,
    field: "voiceNote" | "image" | "demoSnippet"
  ) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const reader = new FileReader();
    reader.onloadend = () => {
      formik.setFieldValue(field, reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const renderAudioUploader = (
    field: "voiceNote" | "demoSnippet",
    label: string
  ) => {
    const value = formik.values[field];
    const inputId = `${field}-upload`;
    const hasFile = typeof value === "string" && value;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <ExperienceFieldLabel darkMode={darkMode}>{label}</ExperienceFieldLabel>
        {hasFile ? (
          <ExperienceMediaCard darkMode={darkMode}>
            <Box sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <audio
                controls
                src={value as string}
                style={{ flex: 1, height: 36 }}
              />
              <IconButton
                onClick={() => formik.setFieldValue(field, "")}
                sx={getFloatingActionSx(darkMode)}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </ExperienceMediaCard>
        ) : null}
        <input
          id={inputId}
          type="file"
          accept="audio/*"
          hidden
          onChange={(e) => {
            handleFileSelect(e.target.files, field);
            e.target.value = "";
          }}
        />
        <label htmlFor={inputId}>
          <Button
            variant="contained"
            component="span"
            sx={{ ...getPrimaryButtonSx(darkMode), alignSelf: "flex-start" }}
          >
            {hasFile ? t("replace_audio") : t("upload_audio")}
          </Button>
        </label>
      </Box>
    );
  };

  const renderImageUploader = () => {
    const value = formik.values.image;
    const inputId = "bonus-image-upload";
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <ExperienceFieldLabel darkMode={darkMode}>
          {t("bonus_hidden_image")}
        </ExperienceFieldLabel>
        {typeof value === "string" && value ? (
          <ExperienceMediaCard darkMode={darkMode}>
            <Box sx={{ position: "relative" }}>
              <Box
                component="img"
                src={value}
                alt={t("bonus_image_alt")}
                sx={{
                  width: "100%",
                  height: 180,
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <IconButton
                onClick={() => formik.setFieldValue("image", "")}
                sx={{
                  ...getFloatingActionSx(darkMode),
                  position: "absolute",
                  top: 8,
                  right: 8,
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </ExperienceMediaCard>
        ) : null}
        <input
          id={inputId}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            handleFileSelect(e.target.files, "image");
            e.target.value = "";
          }}
        />
        <label htmlFor={inputId}>
          <Button
            variant="contained"
            component="span"
            sx={{ ...getPrimaryButtonSx(darkMode), alignSelf: "flex-start" }}
          >
            {value ? t("replace_image") : t("upload_image")}
          </Button>
        </label>
      </Box>
    );
  };

  const cardLinks = formik.values.cardLinks || [];
  const selectedCardIds = useMemo(
    () =>
      new Set(
        cardLinks
          .map((card) =>
            card.card_id != null ? String(card.card_id) : undefined
          )
          .filter(Boolean)
      ),
    [cardLinks]
  );
  const linkedCardOptions = useMemo(() => {
    const selectedWithoutOption = cardLinks.filter(
      (selectedCard) =>
        selectedCard.card_id == null ||
        !availableCards.some(
          (option) => String(option.card_id) === String(selectedCard.card_id)
        )
    );

    return [...availableCards, ...selectedWithoutOption];
  }, [availableCards, cardLinks]);

  const cardFallback = t("bonus_card_fallback");

  return (
    <FormikProvider value={formik}>
      <ExperienceShell>
        {!isSeason && (
          <ExperienceHeader
            darkMode={darkMode}
            icon={Gift}
            title={t("bonus")}
            subtitle={t("bonus_header_subtitle")}
          />
        )}

        <ExperienceFieldGuide darkMode={darkMode}>
          {isSeason
            ? t("bonus_field_guide_season")
            : t("bonus_field_guide")}
        </ExperienceFieldGuide>

        <TextField
          fullWidth
          multiline
          minRows={4}
          label={
            <>
              {isSeason ? t("bonus_description_label_season") : t("bonus_description_label")}
              {isSeason && <ExperienceOptionalLabel darkMode={darkMode} />}
            </>
          }
          name="description"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={t("bonus_description_placeholder")}
          error={Boolean(
            formik.touched.description && formik.errors.description
          )}
          helperText={fieldHelperText(
            formik.touched.description && formik.errors.description
              ? String(formik.errors.description)
              : undefined,
            t("bonus_description_helper")
          )}
          sx={fieldSx}
        />

        <TextField
          label={t("bonus_title")}
          name="title"
          value={formik.values.title}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(formik.touched.title && formik.errors.title)}
          helperText={fieldHelperText(
            formik.touched.title && formik.errors.title
              ? String(formik.errors.title)
              : undefined,
            " "
          )}
          fullWidth
          sx={fieldSx}
        />

        {renderAudioUploader("voiceNote", t("bonus_voice_note"))}

        {renderImageUploader()}

        {renderAudioUploader("demoSnippet", t("bonus_demo_snippet"))}

        <ExperienceFieldLabel darkMode={darkMode}>
          {t("bonus_linked_cards")}
        </ExperienceFieldLabel>
        <Autocomplete
              multiple
              options={linkedCardOptions}
              value={cardLinks}
              loading={isCardsLoading}
              filterSelectedOptions
              isOptionEqualToValue={(option, value) => {
                if (option.card_id != null && value.card_id != null) {
                  return String(option.card_id) === String(value.card_id);
                }
                return (
                  option.card_name === value.card_name &&
                  option.card_type === value.card_type
                );
              }}
              getOptionLabel={(option) =>
                option.card_type
                  ? `${option.card_name || cardFallback} (${formatCardTypeLabel(option.card_type)})`
                  : option.card_name || cardFallback
              }
              onChange={(_, selectedCards) => {
                formik.setFieldValue(
                  "cardLinks",
                  selectedCards.map(normalizeLinkedCard)
                );
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      width: "100%",
                    }}
                  >
                    {option.thumbnail_url ? (
                      <Box
                        component="img"
                        src={option.thumbnail_url}
                        alt={option.card_name || cardFallback}
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 1.5,
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 1.5,
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: darkMode
                            ? "var(--neutral-700)"
                            : "var(--neutral-100)",
                          color: darkMode
                            ? "var(--neutral-200)"
                            : "var(--neutral-500)",
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </Box>
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: darkMode
                            ? "var(--neutral-100)"
                            : "var(--neutral-900)",
                        }}
                      >
                        {option.card_name || cardFallback}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: darkMode
                            ? "var(--neutral-400)"
                            : "var(--neutral-600)",
                        }}
                      >
                        {formatCardTypeLabel(option.card_type) || cardFallback}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("bonus_select_linked_cards")}
                  helperText={t("bonus_linked_cards_helper")}
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isCardsLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
        {cardLinks.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, minmax(0, 1fr))",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              gap: 1.25,
            }}
          >
            {cardLinks.map((link, index) => (
              <ExperienceMediaCard
                key={getLinkedCardKey(link, index)}
                darkMode={darkMode}
              >
                <Box
                  sx={{
                    p: 1.25,
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                    {link.thumbnail_url ? (
                      <Box
                        component="img"
                        src={link.thumbnail_url}
                        alt={link.card_name || t("bonus_linked_card_alt")}
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 1.5,
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 1.5,
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: darkMode
                            ? "var(--neutral-700)"
                            : "var(--neutral-100)",
                          color: darkMode
                            ? "var(--neutral-200)"
                            : "var(--neutral-500)",
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: darkMode
                            ? "var(--neutral-100)"
                            : "var(--neutral-900)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {link.card_name || cardFallback}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: darkMode
                            ? "var(--neutral-400)"
                            : "var(--neutral-600)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {formatCardTypeLabel(link.card_type) || cardFallback}
                      </Typography>
                      {link.card_id != null && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: darkMode
                              ? "var(--neutral-500)"
                              : "var(--neutral-500)",
                          }}
                        >
                          {t("bonus_card_id", { id: link.card_id })}
                        </Typography>
                      )}
                    </Box>
                  <IconButton
                    size="small"
                    onClick={() =>
                      formik.setFieldValue(
                        "cardLinks",
                        cardLinks.filter(
                          (_, currentIndex) => currentIndex !== index
                        )
                      )
                    }
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ExperienceMediaCard>
            ))}
          </Box>
        )}
        {!isCardsLoading &&
          availableCards.length === 0 &&
          !selectedCardIds.size && (
            <Typography variant="caption" sx={{ color: theme.textMuted }}>
              {t("bonus_no_cards_available")}
            </Typography>
          )}
      </ExperienceShell>
    </FormikProvider>
  );
};

export default BonusExperience;
